import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Users, Sparkles, BarChart3, Globe, FileText, Brain, MessageSquare } from "lucide-react";
import UsersPage from "./UsersPage";
import AdminStatsPage from "./AdminStatsPage";
import { WorkflowDiagram, PromptsList } from "@/components/prompts";
import { WORKFLOWS, WORKFLOW_ORDER, type WorkflowId } from "@/data/prompts";

const WORKFLOW_ICONS: Record<string, React.ElementType> = {
  globe: Globe,
  file: FileText,
  brain: Brain,
  chat: MessageSquare,
};

type AdminTab = "users" | "prompts" | "stats";
type PromptsView = "workflows" | "all";

export default function AdminPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab: AdminTab = location.pathname.includes("/admin/prompts")
    ? "prompts"
    : location.pathname.includes("/admin/stats")
      ? "stats"
      : "users";
  const [promptsView, setPromptsView] = useState<PromptsView>("workflows");
  const [activeWorkflow, setActiveWorkflow] = useState<WorkflowId>("web_crawler");

  const setTab = (tab: AdminTab) => {
    navigate(`/admin/${tab}`, { replace: true });
  };

  return (
    <div>
      {/* Top-level tabs bar */}
      <div className="px-6 pt-6 max-w-7xl mx-auto">
        <div className="flex gap-1 mb-0 border-b border-border">
          <button
            onClick={() => setTab("users")}
            className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
              activeTab === "users"
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users
            </span>
            {activeTab === "users" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
          <button
            onClick={() => setTab("prompts")}
            className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
              activeTab === "prompts"
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Prompts & Workflows
            </span>
            {activeTab === "prompts" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
          <button
            onClick={() => setTab("stats")}
            className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
              activeTab === "stats"
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Rate Limits
            </span>
            {activeTab === "stats" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
            )}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "users" && <UsersPage />}
      {activeTab === "prompts" && (
        <div className="p-6 max-w-7xl mx-auto">
          <PromptsTab
            view={promptsView}
            onViewChange={setPromptsView}
            activeWorkflow={activeWorkflow}
            onWorkflowChange={setActiveWorkflow}
          />
        </div>
      )}
      {activeTab === "stats" && (
        <div className="p-6 max-w-7xl mx-auto">
          <AdminStatsPage />
        </div>
      )}
    </div>
  );
}

function PromptsTab({
  view,
  onViewChange,
  activeWorkflow,
  onWorkflowChange,
}: {
  view: PromptsView;
  onViewChange: (v: PromptsView) => void;
  activeWorkflow: WorkflowId;
  onWorkflowChange: (id: WorkflowId) => void;
}) {
  const workflow = WORKFLOWS[activeWorkflow];

  return (
    <div>
      {/* View toggle */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex bg-muted rounded-lg p-0.5">
          <button
            onClick={() => onViewChange("workflows")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              view === "workflows"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Workflows
          </button>
          <button
            onClick={() => onViewChange("all")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              view === "all"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            All Prompts
          </button>
        </div>
      </div>

      {view === "workflows" && (
        <div>
          {/* Workflow sub-tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
            {WORKFLOW_ORDER.map(wfId => {
              const wf = WORKFLOWS[wfId];
              const Icon = WORKFLOW_ICONS[wf.icon] || Sparkles;
              const isActive = activeWorkflow === wfId;
              return (
                <button
                  key={wfId}
                  onClick={() => onWorkflowChange(wfId)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? "bg-primary/10 text-primary border border-primary/30"
                      : "bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {wf.name}
                </button>
              );
            })}
          </div>

          {/* Workflow info */}
          <div className="mb-6 p-4 bg-card border border-border rounded-lg">
            <h3 className="text-lg font-semibold text-foreground mb-1">{workflow.name}</h3>
            <p className="text-sm text-muted-foreground">{workflow.description}</p>
          </div>

          {/* Workflow diagram */}
          <WorkflowDiagram workflow={workflow} />
        </div>
      )}

      {view === "all" && <PromptsList />}
    </div>
  );
}
